/**
 * MagicBI Design System - Modal Component
 * 
 * Accessible modal component with multiple sizes,
 * animations, and portal rendering.
 */

import React, { useEffect, useRef, useState, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, keyframes } from 'styled-components';

// Modal size types
type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'workspace' | 'compact' | 'composer' | 'full';

/**
 * Viewport coordinates for a modal anchored to a trigger. Either `top` or
 * `bottom` is supplied, matching the trigger menu's placement when it flips.
 */
export interface ModalAnchor {
  top?: number;
  bottom?: number;
  right: number;
}

// Modal props interface
export interface ModalProps {
  /** Whether modal is open */
  open: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Modal size */
  size?: ModalSize;
  /**
   * Opt-in CSS width cap (e.g. "56.25rem"). When set, the dialog is `min(90%, width)` wide and as
   * tall as its content (up to 90vh), whatever the `size`.
   */
  width?: string;
  /** Whether clicking backdrop closes modal */
  closeOnBackdropClick?: boolean;
  /** Whether pressing escape closes modal */
  closeOnEscape?: boolean;
  /** Whether modal can be closed */
  closable?: boolean;
  /** Custom backdrop color */
  backdropColor?: string;
  /** Whether to show backdrop */
  showBackdrop?: boolean;
  /** Animation duration */
  animationDuration?: string;
  /** Z-index override */
  zIndex?: number;
  /** Portal container */
  container?: HTMLElement;
  /** Whether to prevent body scroll */
  preventBodyScroll?: boolean;
  /** Whether to focus modal on open */
  autoFocus?: boolean;
  /** Custom class name */
  className?: string;
  /** ID of the visible title that labels the dialog. */
  ariaLabelledBy?: string;
  /** Optional trigger-relative placement instead of the default centered dialog. */
  anchor?: ModalAnchor;
  /** Pins the dialog this far from the top (CSS length), horizontally centred, instead of centring it vertically. */
  pinnedTop?: string;
  /** Children elements */
  children: React.ReactNode;
}

// Modal header props
export interface ModalHeaderProps {
  children: React.ReactNode;
  /** Whether to show close button */
  showCloseButton?: boolean;
  /** Close button callback */
  onClose?: () => void;
  /** Custom padding override */
  padding?: string;
  /** Optional fixed header height (e.g. Figma workspace wizard 64px row). */
  minHeight?: string;
  /** Custom class name */
  className?: string;
}

// Modal body props
export interface ModalBodyProps {
  children: React.ReactNode;
  /** Custom padding */
  padding?: string;
  /** Custom class name */
  className?: string;
  /**
   * Vertical overflow for the body region. Default "auto" scrolls the full body
   * when content exceeds the modal. Use "hidden" when an inner region owns scroll
   * (e.g. a split form + preview with only one pane scrolling), and "visible"
   * when the body holds a popover that must overlay the footer or the shell
   * edge — "auto" and "hidden" both clip it, and no z-index escapes a scroll
   * container. Only safe for a body whose content cannot exceed the modal's
   * height, since "visible" gives up the scroll fallback.
   */
  overflowY?: "auto" | "hidden" | "visible";
}

// Modal footer props
export interface ModalFooterProps {
  children: React.ReactNode;
  /** Custom padding override */
  padding?: string;
  /** Flex gap between footer children (defaults to theme modal gap). */
  gap?: string;
  /** Custom class name */
  className?: string;
}

// Animation keyframes
const backdropFadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const backdropFadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const modalEase = "cubic-bezier(0.4, 0, 0.2, 1)";

const modalSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, calc(-50% + 10px)) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

const modalSlideOut = keyframes`
  from {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
  to {
    opacity: 0;
    transform: translate(-50%, calc(-50% + 10px)) scale(0.98);
  }
`;

const pinnedModalSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, 0.625rem) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0) scale(1);
  }
`;

const pinnedModalSlideOut = keyframes`
  from {
    opacity: 1;
    transform: translate(-50%, 0) scale(1);
  }
  to {
    opacity: 0;
    transform: translate(-50%, 0.625rem) scale(0.98);
  }
`;

const anchoredModalSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(0.625rem) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const anchoredModalSlideOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(0.625rem) scale(0.98);
  }
`;

// Get size styles
const getSizeStyles = (size: ModalSize) => {
  switch (size) {
    case 'sm':
      return css`
        width: 90%;
        max-width: 400px;
        max-height: 90vh;
      `;

    case 'md':
      return css`
        width: 90%;
        max-width: 600px;
        max-height: 90vh;
      `;

    case 'lg':
      return css`
        width: 90%;
        max-width: 800px;
        max-height: 90vh;
      `;

    case 'xl':
      return css`
        width: 90%;
        max-width: 1200px;
        max-height: 90vh;
      `;

    /** Figma workspace wizard frame (node 2858:215378) — 880×568 fixed shell. */
    case 'workspace':
      return css`
        width: min(90%, 880px);
        max-width: 880px;
        height: min(568px, 90vh);
        max-height: 90vh;
      `;

    /** F18 Export dialog: 420px wide (the card sits inside this box, so the cap must be here). */
    case 'compact':
      return css`
        width: min(90%, 26.25rem);
        max-width: 26.25rem;
        max-height: 90vh;
      `;

    /** Deliver dialog: 1080px two-pane composer (widened from the F18 860px so the preview pane fits the email layout). */
    case 'composer':
      return css`
        width: min(90%, 67.5rem);
        max-width: 67.5rem;
        max-height: 90vh;
      `;

    case 'full':
      return css`
        width: 95vw;
        height: 95vh;
        max-width: none;
        max-height: none;
      `;

    default:
      return css``;
  }
};

// Styled backdrop
const StyledBackdrop = styled.div<{
  $open: boolean;
  $animationDuration: string;
  $backdropColor?: string;
  $zIndex: number;
}>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ $backdropColor, theme }) => 
    $backdropColor || theme.colors.surface.overlay
  };
  z-index: ${({ $zIndex }) => $zIndex};
  
  ${({ $open, $animationDuration }) => $open ? css`
    animation: ${backdropFadeIn} ${$animationDuration} ${modalEase};
  ` : css`
    animation: ${backdropFadeOut} ${$animationDuration} ${modalEase};
  `}
`;

// Styled modal container
const StyledModalContainer = styled.div<{
  $open: boolean;
  $size: ModalSize;
  $width?: string;
  $animationDuration: string;
  $zIndex: number;
  $anchor?: ModalAnchor;
  $pinnedTop?: string;
}>`
  position: fixed;
  top: ${({ $anchor, $pinnedTop }) => ($pinnedTop ? $pinnedTop : $anchor?.top == null ? ($anchor ? "auto" : "50%") : `${$anchor.top}px`)};
  bottom: ${({ $anchor }) => ($anchor?.bottom == null ? "auto" : `${$anchor.bottom}px`)};
  right: ${({ $anchor }) => ($anchor ? `${$anchor.right}px` : "auto")};
  left: ${({ $anchor }) => ($anchor ? "auto" : "50%")};
  transform: ${({ $anchor, $pinnedTop }) => ($anchor ? "none" : $pinnedTop ? "translateX(-50%)" : "translate(-50%, -50%)")};
  z-index: ${({ $zIndex }) => $zIndex + 1};
  
  /* Size styles */
  ${({ $size }) => getSizeStyles($size)}
  ${({ $width }) =>
    $width
      ? css`
          width: min(90%, ${$width});
          max-width: none;
          height: auto;
          max-height: 90vh;
        `
      : ''}
  
  /* Animation */
  ${({ $open, $animationDuration, $anchor, $pinnedTop }) => $open ? css`
    animation: ${$anchor ? anchoredModalSlideIn : $pinnedTop ? pinnedModalSlideIn : modalSlideIn} ${$animationDuration} ${modalEase};
  ` : css`
    animation: ${$anchor ? anchoredModalSlideOut : $pinnedTop ? pinnedModalSlideOut : modalSlideOut} ${$animationDuration} ${modalEase};
  `}
`;

// Styled modal content
const StyledModal = styled.div`
  background-color: ${({ theme }) => theme.colors.surface.primary};
  border-radius: ${({ theme }) => theme.borders.semantic.modal.default};
  box-shadow: ${({ theme }) => theme.shadows.component.modal};
  display: flex;
  flex-direction: column;
  /* Inherit the container's max-height (e.g. 90vh for xl). Percentage
     values don't resolve here because the parent only has a max-height,
     not a definite height. */
  max-height: inherit;
  height: inherit;
  overflow: hidden;
  outline: none;
`;

// Modal header
const StyledModalHeader = styled.div<{
  $padding?: string;
  $minHeight?: string;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ $padding, theme }) =>
    $padding || theme.spacing.component.modal.padding};
  ${({ $minHeight }) =>
    $minHeight
      ? css`
          box-sizing: border-box;
          height: ${$minHeight};
          min-height: ${$minHeight};
        `
      : css`
          min-height: auto;
        `}
  box-shadow: ${({ theme }) => theme.shadows.elevation[2]};
  flex-shrink: 0;
  position: relative;
  z-index: 1;
`;

// Close button
const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borders.radius.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: ${({ theme }) => theme.transitions.semantic.interactive.button.all};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.surface.secondary};
    color: ${({ theme }) => theme.colors.text.primary};
  }
  
  &:focus {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadows.interactive.focus};
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

// Modal body
const StyledModalBody = styled.div<{
  $padding?: string;
  $overflowY: "auto" | "hidden" | "visible";
}>`
  flex: 1;
  min-height: 0;
  overflow-y: ${({ $overflowY }) => $overflowY};
  padding: ${({ $padding, theme }) => $padding || theme.spacing.component.modal.padding};

  ${({ $overflowY }) =>
    $overflowY === "hidden" &&
    css`
      display: flex;
      flex-direction: column;
    `}
`;

// Modal footer
const StyledModalFooter = styled.div<{ $padding?: string; $gap?: string }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ $gap, theme }) => $gap ?? theme.spacing.component.modal.gap};
  padding: ${({ $padding, theme }) =>
    $padding || theme.spacing.component.modal.padding};
  border-top: 1px solid ${({ theme }) => theme.colors.border.subtle};
  flex-shrink: 0;
`;

// Modal hook for managing body scroll
const useBodyScrollLock = (lock: boolean) => {
  useEffect(() => {
    if (lock) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [lock]);
};

// Modal hook for escape key handling
const useEscapeKey = (callback: () => void, enabled: boolean) => {
  useEffect(() => {
    if (!enabled) return;
    
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        callback();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [callback, enabled]);
};

// Focus trap hook
const useFocusTrap = (containerRef: React.RefObject<HTMLElement>, active: boolean) => {
  useEffect(() => {
    if (!active || !containerRef.current) return;
    
    const container = containerRef.current;
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
    
    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };
    
    container.addEventListener('keydown', handleTab);
    
    // Focus first element
    firstElement?.focus();
    
    return () => {
      container.removeEventListener('keydown', handleTab);
    };
  }, [containerRef, active]);
};

// Main Modal component
export const Modal = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      open,
      onClose,
      size = 'md',
      width,
      closeOnBackdropClick = true,
      closeOnEscape = true,
      closable = true,
      backdropColor,
      showBackdrop = true,
      animationDuration = '200ms',
      zIndex = 1400,
      container,
      preventBodyScroll = true,
      autoFocus = true,
      className,
      ariaLabelledBy,
      anchor,
      pinnedTop,
      children,
    },
    ref
  ) => {
    const [isVisible, setIsVisible] = useState(false);
    const [shouldRender, setShouldRender] = useState(false);
    const modalRef = useRef<HTMLDivElement>(null);
    
    // Merge refs
    const mergedRef = (element: HTMLDivElement) => {
      if (modalRef.current) modalRef.current = element;
      if (typeof ref === 'function') ref(element);
      else if (ref) ref.current = element;
    };
    
    // Handle open/close animations
    useEffect(() => {
      if (open) {
        setShouldRender(true);
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      } else {
        setIsVisible(false);
        const timer = setTimeout(() => {
          setShouldRender(false);
        }, parseInt(animationDuration));
        
        return () => clearTimeout(timer);
      }
    }, [open, animationDuration]);
    
    // Body scroll lock
    useBodyScrollLock(preventBodyScroll && open);
    
    // Escape key handling
    useEscapeKey(() => {
      if (closable) onClose();
    }, closeOnEscape && open);
    
    // Focus trap
    useFocusTrap(modalRef, autoFocus && open);
    
    // Handle backdrop click
    const handleBackdropClick = (e: React.MouseEvent) => {
      if (e.target === e.currentTarget && closeOnBackdropClick && closable) {
        onClose();
      }
    };
    
    if (!shouldRender) return null;
    
    const modalContent = (
      <>
        {showBackdrop && (
          <StyledBackdrop
            $open={isVisible}
            $animationDuration={animationDuration}
            $backdropColor={backdropColor}
            $zIndex={zIndex}
            onClick={handleBackdropClick}
          />
        )}
        
        <StyledModalContainer
          $open={isVisible}
          $size={size}
          $width={width}
          $animationDuration={animationDuration}
          $zIndex={zIndex}
          $anchor={anchor}
          $pinnedTop={pinnedTop}
        >
          <StyledModal
            ref={mergedRef}
            className={className}
            role="dialog"
            aria-modal="true"
            aria-labelledby={ariaLabelledBy}
            tabIndex={-1}
          >
            {children}
          </StyledModal>
        </StyledModalContainer>
      </>
    );
    
    return createPortal(
      modalContent,
      container || document.body
    );
  }
);

Modal.displayName = 'Modal';

// Modal subcomponents
export const ModalHeader: React.FC<ModalHeaderProps> = ({
  children,
  showCloseButton = true,
  onClose,
  padding,
  minHeight,
  className,
}) => (
  <StyledModalHeader
    $padding={padding}
    $minHeight={minHeight}
    className={className}
  >
    <div>{children}</div>
    {showCloseButton && onClose && (
      <CloseButton onClick={onClose} aria-label="Close modal">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </CloseButton>
    )}
  </StyledModalHeader>
);

export const ModalBody: React.FC<ModalBodyProps> = ({
  children,
  padding,
  className,
  overflowY = "auto",
}) => (
  <StyledModalBody
    $padding={padding}
    $overflowY={overflowY}
    className={className}
  >
    {children}
  </StyledModalBody>
);

export const ModalFooter: React.FC<ModalFooterProps> = ({
  children,
  padding,
  gap,
  className,
}) => (
  <StyledModalFooter $padding={padding} $gap={gap} className={className}>
    {children}
  </StyledModalFooter>
);

export default Modal;
